<template>
  <div class="flex flex-col gap-3 p-4">
    <p
      v-if="moveError"
      class="text-sm text-red-700"
      :role="moveErrorAnnounced ? undefined : 'alert'"
      data-testid="history-error"
    >
      {{ moveError.error }}
      <span v-if="moveError.hint" class="block text-red-600">{{
        moveError.hint
      }}</span>
    </p>

    <div
      v-if="contributors.length"
      class="flex items-center gap-2 border-b border-gray-100 pb-3"
    >
      <DashboardContributors :contributors="contributors" />
      <span class="text-xs text-gray-600" aria-hidden="true">
        {{ contributorCount(contributors) }} in the last
        {{ CONTRIBUTORS_WINDOW_DAYS }} days
      </span>
    </div>

    <!-- An undo, a redo or a settings change adds no entry below, though
         the dashboard list names its author as the last to modify it. -->
    <p
      v-if="untrackedChange"
      class="flex min-w-0 items-center gap-1.5 text-xs text-gray-600"
      data-testid="history-last-change"
    >
      <Avatar
        :user="authorAvatarUser(untrackedChange.author, users.data)"
        size="size-5"
        font-size="text-[9px]"
        class="shrink-0"
        aria-hidden="true"
      />
      <span class="truncate"
        ><span class="text-gray-900">{{ untrackedChange.text }}</span> ·
        {{ authorName(untrackedChange.author) ?? "Unknown" }} ·
        {{ untrackedChange.when }}</span
      >
    </p>

    <p
      v-if="query.isPending && !retrying"
      class="text-sm text-gray-600"
      role="status"
    >
      Loading history…
    </p>

    <!-- Kept up while a retry is on its way, which a query with no data
         otherwise shows as loading: Try again would vanish under the focus. -->
    <div
      v-else-if="(query.isError || retrying) && !entries.length"
      class="flex flex-col items-start gap-2"
      data-testid="history-load-error"
    >
      <p class="text-sm text-red-700" role="alert">
        The history could not be loaded.
      </p>
      <button
        class="btn-secondary"
        :aria-disabled="retrying"
        data-testid="history-retry"
        @click="retry"
      >
        Try again
      </button>
    </div>

    <p
      v-else-if="!entries.length"
      class="text-sm text-gray-600"
      data-testid="history-empty"
    >
      No changes recorded yet. Edits from here on will show up in this list.
    </p>

    <ol v-else class="flex flex-col gap-1" data-testid="history-list">
      <li
        v-for="entry in entries"
        :key="entry.seq"
        class="group flex items-start gap-2 rounded-md px-3 py-2 transition"
        :class="
          entry.isCurrent
            ? 'bg-purple-50 ring-1 ring-purple-200'
            : 'hover:bg-gray-50'
        "
        :aria-current="entry.isCurrent || undefined"
        :data-undone="isUndone(entry) || undefined"
        :data-testid="`history-entry-${entry.seq}`"
      >
        <div class="min-w-0 flex-1">
          <span class="flex items-center justify-between gap-2">
            <span
              class="truncate text-sm font-medium"
              :class="isUndone(entry) ? 'text-gray-500' : 'text-gray-900'"
            >
              {{ describe(entry) }}
            </span>
            <span
              v-if="entry.isCurrent"
              class="shrink-0 text-xs font-semibold text-purple-800"
              >Showing</span
            >
            <span
              v-else-if="isUndone(entry)"
              class="shrink-0 text-xs font-semibold text-gray-500"
              >Undone</span
            >
          </span>
          <!-- Nobody and no date for a layout recovered from the undo
               history: the seeding migration knows neither, and stored the
               dashboard's creation in their place. -->
          <span
            v-if="!isRecoveredFromUndoHistory(entry)"
            class="mt-1 flex items-center gap-1.5 text-xs"
            :class="isUndone(entry) ? 'text-gray-500' : 'text-gray-600'"
            :data-testid="`history-author-${entry.seq}`"
            :data-ai="isAiAuthor(entry) || undefined"
          >
            <!-- Hidden: its alt text is the name the line beside it says. -->
            <Avatar
              :user="authorAvatarUser(entry, users.data)"
              size="size-5"
              font-size="text-[9px]"
              class="shrink-0"
              aria-hidden="true"
            />
            <span class="truncate">{{ who(entry) }} · {{ when(entry) }}</span>
          </span>
          <span
            v-if="replacedByEdit(entry)"
            class="mt-1 block text-xs text-gray-600"
            data-testid="history-replaced-note"
          >
            Replaced by your unsaved edit
          </span>
          <span
            v-if="isFloor(entry)"
            class="mt-1 block text-xs text-gray-600"
            data-testid="history-floor-note"
          >
            Undo stops here. Changes made before this was recorded are not in
            the history.
          </span>
        </div>
        <!-- Shown on hover and focus, and faintly at rest where there is no
             hover. Kept on the entry on screen, and aria-disabled rather
             than disabled: a restore makes its entry the one on screen, and
             a button removed or disabled under the focus drops it to the
             page. -->
        <Tooltip
          placement="left"
          class="shrink-0"
          :triggers="entry.isCurrent ? ['focus'] : undefined"
        >
          <button
            type="button"
            class="rounded p-1 text-gray-600 opacity-0 transition hover:bg-gray-200 hover:text-gray-900 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-purple-900 focus-visible:outline-none aria-disabled:cursor-default aria-disabled:text-gray-400 aria-disabled:hover:bg-transparent"
            :class="
              !entry.isCurrent &&
              'group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-70'
            "
            :aria-disabled="!restorable(entry)"
            :aria-label="`${restoreAction(entry)}: ${describe(entry)}`"
            :data-testid="`history-restore-${entry.seq}`"
            @click="restore(entry)"
          >
            <Icon :name="isUndone(entry) ? 'redo' : 'undo'" class="size-3.5" />
          </button>
          <template #popper>
            <span class="text-xs">{{ restoreHint(entry) }}</span>
          </template>
        </Tooltip>
      </li>
    </ol>

    <p v-if="listCut" class="text-xs text-gray-600" data-testid="history-cut">
      Older changes aren't listed.
      <template v-if="!entries.some((entry) => entry.isCurrent)">
        The version on screen is one of them.
      </template>
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  ref,
  shallowReactive,
  shallowRef,
  watch,
} from "vue";
import { hashKey, useQueryClient } from "@tanstack/vue-query";
import { Tooltip } from "floating-vue";
import { DateTime } from "luxon";
import type {
  AiUpdateStatus,
  DashboardResponse,
} from "@/services/dashboardResponse";
import { useRoute } from "vue-router";
import { useSidepanel } from "@/composables/useSidepanel";
import Avatar from "@/components/Core/Avatar.vue";
import Icon from "@/components/Core/Icon.vue";
import DashboardContributors from "@/views/Dashboard/Components/DashboardContributors.vue";
import { getUserById, useUsersQuery } from "@/services/user";
import {
  authorAvatarUser,
  authorName,
  contributorCount,
  isAiAuthor,
  lastChangeText,
  type RevisionAuthor,
} from "@/views/Dashboard/revisionAuthor";
import {
  CONTRIBUTORS_WINDOW_DAYS,
  type DashboardRevision,
  MAX_REVISIONS_LISTED,
  type RevisionMove,
  type RevisionMoveError,
  revisionEvents,
  revisionMoveErrorFrom,
  UnsavedChangesBlockMove,
  useDashboardRevisionsQuery,
  useRevisionMover,
  useRevisionMoveRequests,
} from "@/services/dashboardRevisions";

const route = useRoute();
const pk = computed(() => Number(route.params.pk));

// The overlay hides a closed panel rather than unmounting it, so this
// would go on listing the history after every save.
const isShown = useSidepanel().isModeOpen("history");
const query = useDashboardRevisionsQuery({ pk, enabled: isShown });

// The dashboard as the editor or the view last read it, never fetched from
// here. An AI run landing, or someone's Approve or Reject, changes it
// without anything telling the history. Read off the cache rather than
// observed: an observer with no fetch of its own hands the shared query its
// options, and every refetch of the dashboard after that fails unsent.
const queryClient = useQueryClient();
const shownModifiedAt = shallowRef<string>();
const shownAiStatus = shallowRef<AiUpdateStatus>();
const shownDashboard = shallowRef<DashboardResponse>();

function shownKey() {
  return ["dashboard", { pk: pk.value }] as const;
}

function readShown(): void {
  const shown = queryClient.getQueryData<DashboardResponse>(shownKey());
  shownModifiedAt.value = shown?.modifiedAt;
  shownAiStatus.value = shown?.aiUpdateStatus;
  shownDashboard.value = shown;
}

watch(pk, readShown, { immediate: true });
const stopReadingShown = queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated") return;
  if (event.query.queryHash === hashKey(shownKey())) readShown();
});
onBeforeUnmount(stopReadingShown);
// The two values, not the dashboard: every save writes a new copy of it.
// A refetch runs even while the query is disabled, so only when shown.
watch([shownModifiedAt, shownAiStatus], () => {
  // One the dashboard's own invalidation has started is fresh enough.
  if (isShown.value) void query.refetch({ cancelRefetch: false });
});
// The org's members, for the authors' pictures. The contributors above read
// the same query.
const users = useUsersQuery();

// By dashboard, as is `panelMoving`: the sidepanel stays open from one
// dashboard to the next, and the next one's panel is not waiting on these.
const retryingPks = shallowReactive(new Set<number>());
const retrying = computed(() => retryingPks.has(pk.value));

async function retry(): Promise<void> {
  if (retrying.value) return;
  const retried = pk.value;
  retryingPks.add(retried);
  try {
    await query.refetch();
  } finally {
    retryingPks.delete(retried);
  }
}

// The editor or the dashboard view, whichever has this dashboard open.
// Moves go through it so they wait for its own writes: the editor's saves,
// the view's date roll.
const editorMover = useRevisionMover(pk);
const requests = useRevisionMoveRequests();
const movingPks = shallowReactive(new Set<number>());
const panelMoving = computed(() => movingPks.has(pk.value));

const moveError = ref<RevisionMoveError | null>(null);
// Not announced again: the failed save already put its own alert on screen.
const moveErrorAnnounced = shallowRef(false);

// The refusal is about the step it was made for. Any move that lands, from
// here or the toolbar, and the next dashboard both leave it behind.
function forgetMoveErrorOn(moved: DashboardResponse): void {
  if (moved.pk === pk.value) moveError.value = null;
}
revisionEvents.on("moved", forgetMoveErrorOn);
onBeforeUnmount(() => revisionEvents.off("moved", forgetMoveErrorOn));
watch(pk, () => {
  moveError.value = null;
});
watch(isShown, (shown) => {
  if (!shown) moveError.value = null;
});

// What the dashboard's last editor did, when it was not an edit: an edit is
// the newest entry, and says so itself.
const untrackedChange = computed(() => {
  const shown = shownDashboard.value;
  if (!shown?.modifiedAction || shown.modifiedAction === "edit") return null;
  if (shown.modifiedBy === null) return null;
  const text = lastChangeText(shown);
  if (!text) return null;
  const author: RevisionAuthor = {
    actor: shown.modifiedBy,
    actorName: getUserById(users.data, shown.modifiedBy)?.name ?? null,
    actorKind: shown.modifiedByAi ? "mcp" : "user",
  };
  return {
    text,
    author,
    when: DateTime.fromISO(shown.modifiedAt).toRelative() ?? "",
  };
});

const entries = computed<DashboardRevision[]>(
  () => query.data?.revisions ?? [],
);
const contributors = computed(() => query.data?.contributors ?? []);
// The server stops at its cap, which a list ending on the oldest revision
// has reached without leaving any out.
const listCut = computed(
  () =>
    entries.value.length >= MAX_REVISIONS_LISTED &&
    entries.value.at(-1)?.seq !== query.data?.oldestSeq,
);
// Until the dashboard has been read there is no `modifiedAt` to send, and a
// move without one is not checked against anybody's save since.
const stamped = computed(() => shownModifiedAt.value !== undefined);
// The editor holds an edit a move would save first, and that save drops
// every revision above the one shown.
const dropsRedo = computed(() => editorMover.value?.dropsRedo?.value ?? false);
const isMoving = computed(
  () => panelMoving.value || (editorMover.value?.moving.value ?? false),
);

async function run(move: RevisionMove): Promise<void> {
  const moved = pk.value;
  moveError.value = null;
  movingPks.add(moved);
  try {
    const editor = editorMover.value;
    if (editor) {
      await editor.move(move);
    } else if (shownModifiedAt.value !== undefined) {
      await requests.send(moved, move, shownModifiedAt.value);
    }
  } catch (error: unknown) {
    // Refused for a dashboard the panel no longer shows.
    if (pk.value !== moved) return;
    // The backend sends `{error, hint}` when a move is refused — a revision
    // that aged out, or an AI run holding the config — and the editor refuses
    // one it could not save ahead of. Each is something the user can act on,
    // which a generic failure is not.
    moveError.value = revisionMoveErrorFrom(error) ?? {
      error: "That change could not be applied.",
    };
    moveErrorAnnounced.value =
      error instanceof UnsavedChangesBlockMove &&
      error.reason === "save-failed";
  } finally {
    movingPks.delete(moved);
  }
}

function restorable(entry: DashboardRevision): boolean {
  return (
    stamped.value &&
    !entry.isCurrent &&
    !isMoving.value &&
    !replacedByEdit(entry)
  );
}

function restore(entry: DashboardRevision): void {
  if (restorable(entry)) void run({ kind: "restore", seq: entry.seq });
}

function isUndone(entry: DashboardRevision): boolean {
  const currentSeq = query.data?.currentSeq ?? null;
  return currentSeq !== null && entry.seq > currentSeq;
}

function replacedByEdit(entry: DashboardRevision): boolean {
  return dropsRedo.value && isUndone(entry);
}

function restoreAction(entry: DashboardRevision): string {
  if (entry.isCurrent) return "On screen";
  return isUndone(entry) ? "Redo to this version" : "Restore this version";
}

function restoreHint(entry: DashboardRevision): string {
  if (entry.isCurrent) return "This version is on screen";
  if (replacedByEdit(entry)) return "Replaced by your unsaved edit";
  return isUndone(entry)
    ? `${restoreAction(entry)}. The versions above stay listed until your next edit.`
    : `${restoreAction(entry)}. Newer versions stay listed until your next edit.`;
}

/** Who made the change. An AI edit names the person it was made for as
 * well, so it does not read as theirs by hand. */
function who(entry: DashboardRevision): string {
  // Null for a seeded entry, a deleted author and every system write, so
  // the kind is what carries the meaning when there is no name.
  const name = authorName(entry);
  if (name) return name;
  if (entry.actorKind !== "system") return "Unknown";
  // The floor and the layout the dashboard held when history started
  // predate it, so nobody is recorded as having made them, including when,
  // as on an AI-built dashboard, somebody did. A later system entry is a
  // repair, an unlock, a date roll or a change our staff made in the admin.
  return isFloor(entry) || isSeededTip(entry)
    ? "Recorded when history started"
    : "Fyr";
}

function isSeededTip(entry: DashboardRevision): boolean {
  return entry.source === "seed:current";
}

function isRecoveredFromUndoHistory(entry: DashboardRevision): boolean {
  return entry.source === "seed:undo-history";
}

function when(entry: DashboardRevision): string {
  return DateTime.fromISO(entry.createdAt).toRelative() ?? "";
}

/** Whether this is the layout the history started from: the oldest
 * revision there is, recorded by the funnel's first-write capture or the
 * seeding migration rather than made by anyone.
 *
 * The kind alone is not enough. Repair and unlock commands write `system`
 * revisions too, later on, and those are ordinary steps with summaries of
 * their own. */
function isFloor(entry: DashboardRevision): boolean {
  return entry.actorKind === "system" && entry.seq === query.data?.oldestSeq;
}

function describe(entry: DashboardRevision): string {
  // Ahead of the stored summary for the floor: that summary says what the
  // row is to the funnel that wrote it ("Layout before the first tracked
  // edit"), which reads as though the layout itself were missing. It is
  // not — the entry holds a full config and restores like any other, and on
  // a dashboard nobody edited before tracking started it is the layout it
  // was created with.
  if (isFloor(entry)) return "Earliest saved layout";
  if (isSeededTip(entry)) return "Layout when history started";
  if (entry.summary) return entry.summary;
  // Without a summary, the kind is all that says what sort of change this
  // was.
  if (entry.actorKind === "user") return "Edited in the dashboard";
  return isAiAuthor(entry) ? "AI change" : "Automatic change";
}
</script>
